import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { teamAPI } from '../lib/api';
import { formatDate, formatCurrency } from '../lib/utils';
import { 
  Users, UserPlus, Mail, Shield, Crown, Briefcase, User, MoreVertical,
  Edit, Trash2, Check, X, RefreshCw, Settings, Clock, Send, Ban,
  Eye, FileText, DollarSign, BarChart3, Wallet, ChevronDown, ChevronRight,
  AlertCircle, Pencil
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';

const ROLES = [
  { value: 'partner', label: 'Partner', icon: Crown, color: 'bg-purple-100 text-purple-800' },
  { value: 'associate', label: 'Associate', icon: Briefcase, color: 'bg-blue-100 text-blue-800' },
  { value: 'paralegal', label: 'Paralegal', icon: Users, color: 'bg-green-100 text-green-800' },
  { value: 'admin', label: 'Admin', icon: Shield, color: 'bg-orange-100 text-orange-800' },
];

const PERMISSION_CATEGORIES = [
  { 
    key: 'clients', 
    label: 'Clients', 
    icon: Users,
    permissions: ['view', 'create', 'edit', 'delete']
  },
  { 
    key: 'cases', 
    label: 'Cases', 
    icon: Briefcase,
    permissions: ['view', 'create', 'edit', 'delete', 'assign']
  },
  { 
    key: 'documents', 
    label: 'Documents', 
    icon: FileText,
    permissions: ['view', 'upload', 'edit', 'delete']
  },
  { 
    key: 'billing', 
    label: 'Billing', 
    icon: DollarSign,
    permissions: ['view', 'create', 'edit', 'delete']
  },
  { 
    key: 'trust', 
    label: 'Trust Accounting', 
    icon: Wallet,
    permissions: ['view', 'create', 'edit']
  },
  { 
    key: 'reports', 
    label: 'Reports', 
    icon: BarChart3,
    permissions: ['view', 'export']
  },
  { 
    key: 'team', 
    label: 'Team Management', 
    icon: Users,
    permissions: ['view', 'invite', 'manage', 'permissions']
  },
  { 
    key: 'settings', 
    label: 'Settings', 
    icon: Settings,
    permissions: ['view', 'edit']
  },
];

export const Team = () => {
  const { user, isOwner, canManageTeam } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [inviteDialog, setInviteDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [editMemberDialog, setEditMemberDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState({});
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'associate' });
  const [editForm, setEditForm] = useState({ role: '', hourly_rate: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamRes, invitationsRes, permissionsRes] = await Promise.all([
        teamAPI.getAll(),
        teamAPI.getInvitations().catch(() => ({ data: [] })),
        teamAPI.getPermissions().catch(() => ({ data: { permissions: {} } }))
      ]);
      setMembers(teamRes.data);
      setInvitations(invitationsRes.data || []);
      setPermissions(permissionsRes.data?.permissions || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await teamAPI.getAll();
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await teamAPI.invite(inviteForm);
      toast.success('Invitation sent successfully');
      setInviteDialog(false);
      setInviteForm({ name: '', email: '', role: 'associate' });
      // Show the setup URL for demo purposes
      toast.info(`Setup URL: ${response.data.setup_url}`, { duration: 10000 });
      // Refresh invitations
      const invRes = await teamAPI.getInvitations();
      setInvitations(invRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    try {
      await teamAPI.updateRole(selectedMember.user_id, selectedMember.newRole);
      toast.success('Role updated successfully');
      setRoleDialog(false);
      setSelectedMember(null);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setEditForm({
      role: member.role,
      hourly_rate: member.hourly_rate || ''
    });
    setEditMemberDialog(true);
  };

  const handleSaveEditMember = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    try {
      // Update role if changed
      if (editForm.role !== selectedMember.role) {
        await teamAPI.updateRole(selectedMember.user_id, editForm.role);
      }
      // Update hourly rate if changed
      if (editForm.hourly_rate && parseFloat(editForm.hourly_rate) !== selectedMember.hourly_rate) {
        await teamAPI.updateHourlyRate(selectedMember.user_id, parseFloat(editForm.hourly_rate));
      }
      toast.success('Team member updated successfully');
      setEditMemberDialog(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name} from the team?`)) return;
    
    try {
      await teamAPI.remove(member.user_id);
      toast.success('Team member removed');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const handleResendInvitation = async (inviteId) => {
    try {
      const response = await teamAPI.resendInvitation(inviteId);
      toast.success('Invitation resent');
      toast.info(`Setup URL: ${response.data.setup_url}`, { duration: 10000 });
      const invRes = await teamAPI.getInvitations();
      setInvitations(invRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (inviteId, name) => {
    if (!window.confirm(`Cancel invitation for ${name}?`)) return;
    try {
      await teamAPI.cancelInvitation(inviteId);
      toast.success('Invitation cancelled');
      const invRes = await teamAPI.getInvitations();
      setInvitations(invRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handlePermissionChange = (role, category, permission, value) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [category]: {
          ...prev[role]?.[category],
          [permission]: value
        }
      }
    }));
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      await teamAPI.updatePermissions(permissions);
      toast.success('Permissions saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const toggleRoleExpanded = (role) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const getRoleInfo = (role) => {
    if (role === 'owner') return { label: 'Owner', icon: Crown, color: 'bg-amber-100 text-amber-800' };
    return ROLES.find(r => r.value === role) || { label: role, icon: User, color: 'bg-slate-100 text-slate-800' };
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const pendingInvitations = invitations.filter(i => i.status === 'pending');

  if (loading) {
    return <PageLoader text="Loading team..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Team</h1>
          <p className="text-slate-500 mt-1">Manage your team members, roles, and permissions</p>
        </div>
        {canManageTeam() && (
          <Button onClick={() => setInviteDialog(true)} className="gap-2" data-testid="invite-team-btn">
            <UserPlus className="h-4 w-4" />
            Invite Team Member
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="members" className="gap-2">
            <Users className="h-4 w-4" />
            Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="gap-2">
            <Mail className="h-4 w-4" />
            Invitations
            {pendingInvitations.length > 0 && (
              <Badge className="ml-1 bg-blue-100 text-blue-800">{pendingInvitations.length}</Badge>
            )}
          </TabsTrigger>
          {isOwner() && (
            <TabsTrigger value="permissions" className="gap-2">
              <Shield className="h-4 w-4" />
              Permissions
            </TabsTrigger>
          )}
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-6">
          {/* Team Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{members.length}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {ROLES.map(role => {
              const count = members.filter(m => m.role === role.value).length;
              const RoleIcon = role.icon;
              return (
                <Card key={role.value}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${role.color.split(' ')[0]}`}>
                        <RoleIcon className={`h-5 w-5 ${role.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{count}</p>
                        <p className="text-xs text-slate-500">{role.label}s</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Team Members List */}
          <Card data-testid="team-members-card">
            <CardHeader>
              <CardTitle className="text-lg">Team Members</CardTitle>
              <CardDescription>All members of your firm</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {members.map((member) => {
                  const roleInfo = getRoleInfo(member.role);
                  const RoleIcon = roleInfo.icon;
                  const isCurrentUser = member.user_id === user?.user_id;
                  
                  return (
                    <div 
                      key={member.user_id} 
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                      data-testid={`team-member-${member.user_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{member.name}</h4>
                            {isCurrentUser && <Badge variant="outline" className="text-xs">You</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">{member.email}</p>
                          <p className="text-xs text-slate-400 mt-1">Joined {formatDate(member.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {member.hourly_rate && (
                          <span className="text-sm text-slate-500">
                            {formatCurrency(member.hourly_rate)}/hr
                          </span>
                        )}
                        <Badge className={roleInfo.color}>
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {roleInfo.label}
                        </Badge>
                        {isOwner() && !isCurrentUser && member.role !== 'owner' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditMember(member)} data-testid={`edit-member-${member.user_id}`}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Member
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setSelectedMember({ ...member, newRole: member.role }); setRoleDialog(true); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Change Role
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleRemove(member)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="mt-6">
          <Card data-testid="invitations-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Team Invitations
              </CardTitle>
              <CardDescription>Manage pending and past invitations</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p>No invitations sent yet</p>
                  <Button 
                    variant="link" 
                    onClick={() => setInviteDialog(true)}
                    className="mt-2"
                  >
                    Send your first invitation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {invitations.map((inv) => {
                    const roleInfo = getRoleInfo(inv.role);
                    const RoleIcon = roleInfo.icon;
                    const expired = isExpired(inv.expires_at);
                    const isPending = inv.status === 'pending';
                    
                    return (
                      <div 
                        key={inv.invite_id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          inv.status === 'accepted' ? 'bg-green-50 border-green-200' :
                          expired ? 'bg-red-50 border-red-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`invitation-${inv.invite_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            inv.status === 'accepted' ? 'bg-green-100' :
                            expired ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {inv.status === 'accepted' ? (
                              <Check className="h-5 w-5 text-green-600" />
                            ) : expired ? (
                              <AlertCircle className="h-5 w-5 text-red-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{inv.name}</h4>
                            <p className="text-sm text-slate-500">{inv.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={`text-xs ${roleInfo.color}`}>
                                <RoleIcon className="h-3 w-3 mr-1" />
                                {roleInfo.label}
                              </Badge>
                              <span className="text-xs text-slate-400">
                                Invited {formatDate(inv.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={
                            inv.status === 'accepted' ? 'border-green-300 text-green-700' :
                            expired ? 'border-red-300 text-red-700' :
                            'border-blue-300 text-blue-700'
                          }>
                            {inv.status === 'accepted' ? 'Accepted' : expired ? 'Expired' : 'Pending'}
                          </Badge>
                          {isPending && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleResendInvitation(inv.invite_id)}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Resend Invitation
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleCancelInvitation(inv.invite_id, inv.name)}
                                  className="text-red-600"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Cancel Invitation
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        {isOwner() && (
          <TabsContent value="permissions" className="mt-6">
            <Card data-testid="permissions-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Role Permissions
                    </CardTitle>
                    <CardDescription>Configure what each role can access and do</CardDescription>
                  </div>
                  <Button 
                    onClick={handleSavePermissions} 
                    disabled={savingPermissions}
                    data-testid="save-permissions-btn"
                  >
                    {savingPermissions ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ROLES.map((role) => {
                    const RoleIcon = role.icon;
                    const isExpanded = expandedRoles[role.value];
                    
                    return (
                      <Collapsible 
                        key={role.value} 
                        open={isExpanded} 
                        onOpenChange={() => toggleRoleExpanded(role.value)}
                      >
                        <CollapsibleTrigger asChild>
                          <div 
                            className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                            data-testid={`role-permissions-${role.value}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${role.color.split(' ')[0]}`}>
                                <RoleIcon className={`h-5 w-5 ${role.color.split(' ')[1]}`} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-slate-900">{role.label}</h4>
                                <p className="text-sm text-slate-500">
                                  {members.filter(m => m.role === role.value).length} members
                                </p>
                              </div>
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 ml-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="grid gap-4">
                              {PERMISSION_CATEGORIES.map((category) => {
                                const CategoryIcon = category.icon;
                                return (
                                  <div key={category.key} className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                      <CategoryIcon className="h-4 w-4" />
                                      {category.label}
                                    </div>
                                    <div className="flex flex-wrap gap-4 ml-6">
                                      {category.permissions.map((perm) => (
                                        <div key={perm} className="flex items-center gap-2">
                                          <Switch
                                            id={`${role.value}-${category.key}-${perm}`}
                                            checked={permissions[role.value]?.[category.key]?.[perm] ?? false}
                                            onCheckedChange={(checked) => 
                                              handlePermissionChange(role.value, category.key, perm, checked)
                                            }
                                          />
                                          <Label 
                                            htmlFor={`${role.value}-${category.key}-${perm}`}
                                            className="text-sm capitalize cursor-pointer"
                                          >
                                            {perm}
                                          </Label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
                
                <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Crown className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Owner Permissions</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Owners always have full access to all features and cannot have their permissions modified.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join your firm</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="inviteName">Full Name</Label>
              <Input
                id="inviteName"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Smith"
                required
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@example.com"
                required
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteRole">Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="h-4 w-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setInviteDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="send-invite-btn">
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>Update role for {selectedMember?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select 
                value={selectedMember?.newRole || ''} 
                onValueChange={(v) => setSelectedMember(prev => ({ ...prev, newRole: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="h-4 w-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setRoleDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateRole} disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Role'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Dialog */}
      <Dialog open={editMemberDialog} onOpenChange={setEditMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update details for {selectedMember?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Member Info Display */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Name:</span>
                <span className="font-medium text-slate-900">{selectedMember?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Email:</span>
                <span className="font-medium text-slate-900">{selectedMember?.email}</span>
              </div>
            </div>
            
            {/* Role Selection */}
            <div className="space-y-2">
              <Label htmlFor="editRole">Role</Label>
              <Select 
                value={editForm.role} 
                onValueChange={(v) => setEditForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="h-4 w-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Hourly Rate */}
            <div className="space-y-2">
              <Label htmlFor="editHourlyRate">Hourly Rate ($)</Label>
              <Input
                id="editHourlyRate"
                type="number"
                value={editForm.hourly_rate}
                onChange={(e) => setEditForm(prev => ({ ...prev, hourly_rate: e.target.value }))}
                placeholder="Enter hourly rate"
                min="0"
                step="0.01"
                data-testid="edit-hourly-rate-input"
              />
              <p className="text-xs text-slate-500">Leave empty to use default rate for the role</p>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditMemberDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveEditMember} disabled={submitting} data-testid="save-edit-member-btn">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
